import { Link } from "react-router-dom";
import { MapPin, Building2, FileText, Hash, Phone, Mail, Link as LinkIcon, ArrowRight } from "lucide-react";
import { STATUS_LABEL, STATUS_TONE, fmtMoney, shortId, relTime } from "./util";
import ContractorBadgeForName from "@/components/admin/ContractorBadgeForName";

export default function LeftColumn({ ws }) {
  const c = ws.case;
  const links = ws.links || {};
  return (
    <div className="space-y-4" data-testid="ws-left-panels">
      <Card title="Job information" testid="ws-job-info">
        <Row label="Job ID"   value={<span className="font-mono text-[12px]">{shortId(c.id)}</span>}/>
        <Row label="Type"     value={(c.case_type || "GENERAL").replace(/_/g, " ")}/>
        <Row label="Concern"  value={c.main_concern || "—"}/>
        <Row label="Quote value" value={fmtMoney(c.quote_value)}/>
        <Row label="Created"  value={relTime(c.created_at)}/>
        <Row label="Updated"  value={relTime(c.updated_at || c.created_at)}/>
        {c.external_job_id && <Row label="External" value={`${c.external_system || ""} ${c.external_job_id}`}/>}
      </Card>

      <Card title="Location" testid="ws-location">
        {c.park_name ? (
          <>
            <div className="flex items-start gap-2">
              <MapPin size={14} className="text-[#B88A2A] mt-0.5 shrink-0"/>
              <div className="min-w-0">
                {links.park_id ? (
                  <Link to={`/adminpanel/assets/parks/${links.park_id}`} className="font-medium text-[#071A33] text-sm hover:text-[#064E2E] hover:underline inline-flex items-center gap-1" data-testid="ws-park-link">
                    {c.park_name} <ArrowRight size={11}/>
                  </Link>
                ) : (
                  <p className="font-medium text-[#071A33] text-sm">{c.park_name}</p>
                )}
                {(c.plot_number || c.town_location) && (
                  <p className="text-xs text-[#475569] mt-0.5">
                    {c.plot_number && links.plot_id ? (
                      <Link to={`/adminpanel/assets/plots/${links.plot_id}`} className="hover:text-[#064E2E] hover:underline" data-testid="ws-plot-link">Plot {c.plot_number}</Link>
                    ) : c.plot_number ? `Plot ${c.plot_number}` : ""}
                    {c.plot_number && c.town_location ? " · " : ""}
                    {c.town_location || ""}
                  </p>
                )}
              </div>
            </div>
          </>
        ) : (
          <p className="text-xs text-[#475569] italic">No location captured.</p>
        )}
        {c.contractor_name && (
          <div className="mt-3 pt-3 border-t border-[#E2DCD0] flex items-start gap-2">
            <Building2 size={14} className="text-[#0F766E] mt-0.5 shrink-0"/>
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569]">Assigned contractor</p>
              {links.contractor_id ? (
                <Link to={`/adminpanel/contractors?focus=${links.contractor_id}`} className="font-medium text-[#071A33] text-sm hover:text-[#064E2E] hover:underline inline-flex items-center gap-1" data-testid="ws-contractor-link">
                  {c.contractor_name} <ArrowRight size={11}/>
                </Link>
              ) : (
                <p className="font-medium text-[#071A33] text-sm">{c.contractor_name}</p>
              )}
              <div className="mt-1.5"><ContractorBadgeForName name={c.contractor_name}/></div>
            </div>
          </div>
        )}
      </Card>

      <Card title="Linked assets" testid="ws-linked-assets">
        {(c.uploaded_files?.length ?? 0) > 0 ? (
          <p className="text-xs text-[#475569]">
            <FileText size={12} className="inline text-[#B88A2A] mr-1"/>
            {c.uploaded_files.length} evidence file{c.uploaded_files.length === 1 ? "" : "s"} attached
            — see Evidence tab.
          </p>
        ) : (
          <p className="text-xs text-[#475569] italic">No files attached yet.</p>
        )}
      </Card>

      <Card title={`Linked jobs (${ws.linked_jobs.length})`} testid="ws-linked-jobs">
        {ws.linked_jobs.length === 0 ? (
          <p className="text-xs text-[#475569] italic">None on this park or contractor.</p>
        ) : (
          <ul className="space-y-2">
            {ws.linked_jobs.map((j) => (
              <li key={j.id}>
                <Link
                  to={`/adminpanel/workspace/${j.id}`}
                  className="block text-xs hover:bg-[#F7F4ED] -mx-2 px-2 py-1.5 rounded transition"
                  data-testid={`ws-linked-job-${shortId(j.id)}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] tracking-[0.1em] text-[#475569]">{shortId(j.id)}</span>
                    <span className={`px-1.5 py-0.5 text-[9px] rounded uppercase tracking-[0.08em] border ${STATUS_TONE[j.status] || STATUS_TONE.NEW}`}>
                      {STATUS_LABEL[j.status] || j.status}
                    </span>
                  </div>
                  <p className="text-[#071A33] truncate mt-1">{j.full_name || "—"}</p>
                  {(j.park_name || j.contractor_name) && (
                    <p className="text-[10px] text-[#475569] truncate mt-0.5">{j.park_name || j.contractor_name}</p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bg-white border border-[#E2DCD0] rounded-lg p-4" data-testid={testid}>
      <h3 className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-medium mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-sm">
      <span className="text-xs text-[#475569]">{label}</span>
      <span className="text-[#071A33] font-medium text-right">{value || "—"}</span>
    </div>
  );
}
